import type { Combatant, EncounterState, SquareCell, WeaponProfile } from "@lancer-vtt/domain";
import { describeCell, type CarrierNote, type CellDescription } from "../../../routes/home/terrainCellDescription";

/**
 * The **TILE** window body — everything about the square under the cursor.
 *
 * The third of the three things `terrain-legibility` was filed for, in the developer's own words:
 * *"a hovered-tile info pane — not tooltip by the mouse, maybe like a little info pane that tells you
 * everything about the hovered tile."* A fixed pane, deliberately not a mouse-follow tooltip: a
 * tooltip covers the board it is describing and vanishes the moment you look away from it, which is
 * the opposite of what a GM comparing two squares mid-encounter needs.
 *
 * **Every word comes from {@link describeCell}, and none of it is authored here.** The component owns
 * layout and nothing else — the same split `TerrainLegendBody` keeps against `legendFor`, and for the
 * same reason: the wording is part of what the board *says*, so it belongs somewhere it can be pinned
 * without mounting a component (`terrainCellDescription.test.ts` does exactly that, including the
 * SEEKING/ARCING phrasing).
 *
 * ## The two regions are structural, not a formatting choice
 *
 * `terrain-legibility` D5 asks for the authored and resolved registers to be *visually distinct and
 * structurally separate*, and the reason is a legibility one: content that rewrites in place when the
 * mode changes reads as flicker, while content that arrives in its own region reads as additive. So:
 *
 *  - The **authored region always renders, always with the same four channel rows**, in the same
 *    order, whether or not an attack is armed. Its shape does not depend on `armed` at all — a claim
 *    `TileInfo.flow.test.tsx` asserts by node identity rather than trusting this comment.
 *  - The **resolved region is appended below it**, labelled with what it is relative to, and is
 *    absent entirely when no attack is armed (D6 — with no attacker there is nothing honest to say).
 *
 * ## Where it renders
 *
 * On top of the docked Map key panel (`MapKeyPanel.tsx`, `decisions/0191` Ruling 3), above the legend.
 * The panel is an overlay beside the tool rail, so it costs no board width (`terrain-legibility` D9's
 * concern, which first made this a window). Its placement is owed a developer eyeball
 * (`board-tool-rail` 8.1).
 */
export function TileInfoBody({
  state,
  cell,
  armed,
}: {
  state: EncounterState;
  /** The hovered square, or `null` when the cursor is off the board. */
  cell: SquareCell | null;
  /** The armed shot, when an attack is armed. Drives the resolved region's presence (D6). */
  armed?: { attacker: Combatant; weapon: WeaponProfile | null } | null;
}) {
  if (!cell) {
    return (
      <div className="gms-content cw-tile">
        <div className="cw-hint">Hover a square on the board to read it.</div>
      </div>
    );
  }
  const description = describeCell({ state, cell, armed });
  return (
    <div className="gms-content cw-tile">
      <div className="cw-tile-coord" data-tile-key={description.key}>
        {description.key}
      </div>
      <AuthoredRegion description={description} />
      {description.resolved && <ResolvedRegion resolved={description.resolved} />}
    </div>
  );
}

/**
 * The authored register — what is painted here, true no matter who is looking.
 *
 * **The four channel rows are unconditional.** A channel sitting at its open-ground default still
 * renders, dimmed, rather than disappearing: a fixed shape is what lets the reader learn the four
 * positions once instead of re-scanning a list whose length changes per tile, and it is what makes
 * "Cover: None" a real answer rather than an absent row the reader has to interpret.
 *
 * The carrier notes below them ARE presence-driven, and that asymmetry is deliberate — a channel is a
 * question every square answers, while a wall, an object or a cloud is a thing that is either here or
 * is not, and its absence needs no row to say so.
 */
function AuthoredRegion({ description }: { description: CellDescription }) {
  return (
    <section className="cw-tile-authored" data-tile-region="authored">
      {description.tile && <TileHeading tile={description.tile} />}
      <ul className="cw-tile-channels">
        {description.channels.map((line) => (
          <li key={line.channel} className="cw-tile-channel" data-tile-channel={line.channel} data-tile-plain={line.plain}>
            <span className="cw-tile-ch-label">{line.label}</span>
            <span className="cw-tile-ch-value">{line.value}</span>
            {line.detail !== null && <span className="cw-tile-ch-detail">{line.detail}</span>}
          </li>
        ))}
      </ul>
      {description.carriers.length > 0 && (
        <ul className="cw-tile-carriers">
          {description.carriers.map((note) => (
            <CarrierRow key={note.id} note={note} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * What is painted here, by name, and one sentence per property (`terrain-tile-types` Phase 5) —
 * absent on open ground. It leads the authored region because the name is the question a GM asks
 * first ("what is that?"); the channels below answer the rest.
 */
function TileHeading({ tile }: { tile: NonNullable<CellDescription["tile"]> }) {
  return (
    <div className="cw-tile-identity" data-tile-custom={tile.custom}>
      <div className="cw-tile-name">{tile.name}</div>
      {tile.properties.length > 0 && (
        <ul className="cw-tile-props">
          {tile.properties.map((p) => (
            <li key={p.id} className="cw-tile-prop" data-tile-prop={p.id}>
              {p.line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One of the other three carriers standing at this cell — a wall on a boundary, an object, a cloud. */
function CarrierRow({ note }: { note: CarrierNote }) {
  return (
    <li className="cw-tile-carrier" data-tile-carrier={note.carrier} data-tile-carrier-id={note.id}>
      <span className="cw-tile-carrier-label">{note.label}</span>
      <span className="cw-tile-carrier-detail">{note.detail}</span>
    </li>
  );
}

/**
 * The resolved register — what this cell does to **one specific armed shot**.
 *
 * It is its own `<section>` with its own heading precisely so its arrival cannot reflow the region
 * above it (D5). The heading is not decoration either: cover is not a property of a cell, so a degree
 * reported without naming the attacker and weapon it was folded against would be a number the reader
 * could reasonably attach to the wrong thing.
 */
function ResolvedRegion({ resolved }: { resolved: NonNullable<CellDescription["resolved"]> }) {
  return (
    <section className="cw-tile-resolved" data-tile-region="resolved" data-tile-exemption={resolved.exemption ?? "none"}>
      <div className="cw-tile-against">{resolved.against}</div>
      <ul className="cw-tile-lines">
        {resolved.lines.map((line) => (
          <li key={line} className="cw-tile-line">
            {line}
          </li>
        ))}
      </ul>
    </section>
  );
}
